import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import { cancelExpense, decideExpense, loadDirectory, loadExpenses, submitExpense, uploadFile, type ExpenseClaim, type ExpenseStatus } from "../data";
import { money } from "../lib/payslip";
import { downloadText, toCsv } from "../lib/csv";
import { FilePick, FileSlot } from "../components/FileSlot";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const CATEGORIES = ["travel", "transport", "meals", "accommodation", "supplies", "phone", "training", "other"];
export const exTone: Record<ExpenseStatus, string> = { submitted: "warning", manager_approved: "info", approved: "success", rejected: "danger", cancelled: "neutral" };

/** Expense claims for Finance/HR: approve, see what is refunded in payroll, export. */
export function Expenses({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const fin = ws.permissions.has("expenses.manage");
  const [rows, setRows] = useState<ExpenseClaim[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [filter, setFilter] = useState<ExpenseStatus | "waiting" | "all">("waiting");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadExpenses(cid).then((r) => { setRows(r); setSel(new Set()); }).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); void loadDirectory(cid).then(setPeople); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const isWaiting = (r: ExpenseClaim) => r.status === "submitted" || r.status === "manager_approved";
  const shown = (rows ?? []).filter((r) => filter === "all" || (filter === "waiting" ? isWaiting(r) : r.status === filter));
  const decidable = shown.filter((r) => isWaiting(r) && r.employment_id !== ws.membership.employment_id);
  const sum = (xs: ExpenseClaim[]) => xs.reduce((a, r) => a + Number(r.amount), 0);

  async function decide(approve: boolean) {
    setMsg(null);
    try { const n = await decideExpense([...sel], approve, note); setNote(""); await refresh(); setMsg({ ok: true, text: `${n} ${approve ? t("otApprovedN") : t("otRejectedN")}` }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const exportCsv = () => downloadText("expense-claims.csv", toCsv(["Employee", "Date", "Category", "Description", "Amount", "Receipt", "Status", "Refunded in payroll"],
    shown.map((r) => [name(r.employment_id), r.claim_date, r.category, r.description, Number(r.amount).toFixed(2), r.receipt_ref ?? "", r.status, r.payroll_run_id ? "yes" : "no"])));

  return (
    <div className="page">
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="kpis">
        <div className="stat"><span className="stat-label">{t("exWaiting")}</span><span className="stat-value small mono">{money(sum((rows ?? []).filter(isWaiting)))}</span></div>
        <div className="stat"><span className="stat-label">{t("exToRefund")}</span><span className="stat-value small mono">{money(sum((rows ?? []).filter((r) => r.status === "approved" && !r.payroll_run_id)))}</span></div>
        <div className="stat"><span className="stat-label">{t("exRefunded")}</span><span className="stat-value small mono">{money(sum((rows ?? []).filter((r) => r.payroll_run_id)))}</span></div>
      </div>
      <section className="card">
        <div className="card-head">
          <div className="filter-chips">
            {(["waiting", "approved", "rejected", "all"] as const).map((s) => (
              <button key={s} type="button" className={`chip${filter === s ? " current" : ""}`} aria-pressed={filter === s} onClick={() => { setFilter(s); setSel(new Set()); }}>
                {s === "waiting" ? t("exWaitingShort") : s === "all" ? t("alertsAll") : t(`exStatus_${s}` as StringKey)}
              </button>
            ))}
          </div>
          <button type="button" className="btn secondary small" onClick={exportCsv}>CSV</button>
        </div>
        {decidable.length > 0 && (
          <div className="row">
            <label className="check"><input type="checkbox" checked={sel.size > 0 && sel.size === decidable.length}
              onChange={() => setSel(sel.size === decidable.length ? new Set() : new Set(decidable.map((r) => r.id)))} /> {t("otSelectAll")}</label>
            <input placeholder={t("decisionNote")} aria-label={t("decisionNote")} value={note} onChange={(e) => setNote(e.target.value)} />
            <button type="button" className="btn primary small" disabled={sel.size === 0} onClick={() => decide(true)}>{t("approve")} ({sel.size})</button>
            <button type="button" className="btn secondary small" disabled={sel.size === 0} onClick={() => decide(false)}>{t("reject")}</button>
          </div>
        )}
        {rows === null ? <p className="muted">{t("loading")}</p> : shown.length === 0 ? <p className="muted">{t("exNone")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th /><th>{t("employee")}</th><th>{t("date")}</th><th className="hide-sm">{t("exWhat")}</th><th>{t("payAmount")}</th><th>{t("status")}</th></tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.id}>
                <td>{decidable.includes(r) && <input type="checkbox" aria-label={t("otSelect")} checked={sel.has(r.id)}
                  onChange={(e) => { const s = new Set(sel); if (e.target.checked) s.add(r.id); else s.delete(r.id); setSel(s); }} />}</td>
                <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{name(r.employment_id)}</button>
                  {r.receipt_path && <div><FileSlot companyId={ws.membership.company_id} area="receipts" ownerId={r.employment_id} recordId={r.id} path={r.receipt_path} canEdit={false} onChanged={() => undefined} /></div>}</td>
                <td className="mono">{r.claim_date}</td>
                <td className="hide-sm">{t(`exCat_${r.category}` as StringKey)} · {r.description}{r.receipt_ref ? <span className="muted small-note"> · {r.receipt_ref}</span> : null}</td>
                <td className="mono">{money(r.amount)}</td>
                <td><span className={`badge ${exTone[r.status]}`}>{r.payroll_run_id ? t("exPaid") : t(`exStatus_${r.status}` as StringKey)}</span></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        <p className="muted small-note">{fin ? t("exHelpFin") : t("exHelp")}</p>
      </section>
    </div>
  );
}

/** Self-service: claim an expense and follow it until it is refunded in payroll. */
export function MyExpenses({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const emp = ws.membership.employment_id;
  const [rows, setRows] = useState<ExpenseClaim[]>([]);
  const [f, setF] = useState({ date: iso(new Date()), category: "transport", description: "", amount: "", receipt: "" });
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => emp ? loadExpenses(cid, emp).then(setRows) : Promise.resolve();
  useEffect(() => { void refresh(); }, [cid, emp]); // eslint-disable-line react-hooks/exhaustive-deps
  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    let id: string;
    try { id = await submitExpense(cid, f.date, f.category, f.description, Number(f.amount), f.receipt); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); return; }
    let note = t("exSent");
    if (file && emp) { try { await uploadFile(cid, "receipts", emp, id, file); } catch (err) { note = `${t("exSent")} ${t("fileFailedLater")} (${errText(err, "")})`; } }
    setF({ ...f, description: "", amount: "", receipt: "" }); setFile(null); setOpen(false); setMsg({ ok: true, text: note }); await refresh();
  }
  return (
    <section className="card">
      <div className="row spread">
        <h2>{t("myExpenses")}</h2>
        {!open && <button type="button" className="btn secondary small" onClick={() => setOpen(true)}>{t("exClaim")}</button>}
      </div>
      {open && (
        <form className="stack" onSubmit={send}>
          <div className="field-row">
            <label className="field"><span>{t("date")}</span><input type="date" max={iso(new Date())} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></label>
            <label className="field"><span>{t("otKind")}</span>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`exCat_${c}` as StringKey)}</option>)}
              </select></label>
            <label className="field"><span>{t("payAmount")}</span><input type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></label>
          </div>
          <div className="field-row">
            <label className="field"><span>{t("exWhat")}</span><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required /></label>
            <label className="field"><span>{t("exReceipt")}</span><input value={f.receipt} onChange={(e) => setF({ ...f, receipt: e.target.value })} placeholder={t("exReceiptHint")} /></label>
          </div>
          <FilePick file={file} onPick={setFile} />
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button className="btn primary">{t("exSubmit")}</button>
          </div>
        </form>
      )}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {rows.length === 0 ? !open && <p className="muted">{t("exNoneMine")}</p> : (
        <ul className="req-list">
          {rows.slice(0, 20).map((r) => (
            <li key={r.id}>
              <span><strong>{r.description}</strong> · <span className="mono">{money(r.amount)}</span> <span className="muted small-note">· {r.claim_date}{r.decision_note ? ` · “${r.decision_note}”` : ""}</span>
                {emp && <> <FileSlot companyId={cid} area="receipts" ownerId={emp} recordId={r.id} path={r.receipt_path} canEdit={r.status === "submitted" || r.status === "manager_approved"} onChanged={refresh} /></>}</span>
              <span className="row">
                <span className={`badge ${exTone[r.status]}`}>{r.payroll_run_id ? t("exPaid") : t(`exStatus_${r.status}` as StringKey)}</span>
                {(r.status === "submitted" || r.status === "manager_approved") && <button type="button" className="btn secondary small" onClick={async () => { await cancelExpense(r.id); await refresh(); }}>{t("cancelRequest")}</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
